import useArrayState from "@clave/use-array-state";
import useBooleanState from "@clave/use-boolean-state";
import cn from "classnames";
import React, { useCallback, useEffect, useMemo, useState, useRef, } from "react";
import Spinner from "../common/Spinner";
import AuctionLots from "../components/auctionLots/AuctionLots";
import auctionApiClient from "./AuctionApiClient";
import events from "../events";
import styles from "./AuctionListPage.module.scss";
import bidModuleStyles from "../components/auctionLots/BidModule.module.scss";
import { withBidAllContext, useBidAllContext } from "../contexts/BidAllContext";
import useDebounce from "@clave/use-debounce";
import DOMPurify from "dompurify";
import { SourceAuctionIdStorageKey, AuctionListPageReturnUrlStorageKey } from "./contracts";
import logger from "../utils/logger";
import { pushObjectToState } from "../utils/push-state";
import config from "../utils/config";
import FilterItem from "./FilterItem";
import categoriesApiClient from "../clients/CategoriesApiClient";
const objectNumberSortOrderKey = "7";
const ItemsPerPageLimit = 30;
const CHRISTIANIA_TEST_AUCTION_IDS = [100193];
export const AuctionListPage = withBidAllContext(({ name, description, auctionUrl, strings, initialSortOrder, startingSortOrder, startingProductType, initialFilterIds, sortOrders, searchQuery, isSearch, auctionId, categoryId, defaultProductType, typeOfProducts, auctionStatus, auctionRootCategoryId, }) => {
    var _a, _b, _c;
    const bidAllCtx = useBidAllContext();
    const queryStrings = new URLSearchParams(location.search);
    const [filterCategories, setFilterCategories] = useState([]);
    const [loadedCategories, setLoadedCategories] = useArrayState([]);
    const [selectedCategories, setSelectedCategories] = useArrayState(() => initialFilterIds);
    const [showFilterCategories, setShowFilterCategories] = useBooleanState();
    const [minLotNumber, setMinLotNumber] = useState("");
    const [sortOrderOrLotNumberChanged, setSortOrderOrLotNumberChanged] = useState(true);
    const [selectedSortOrder, setSelectedSortOrder] = useState((_a = queryStrings.get("sortOrder")) !== null && _a !== void 0 ? _a : initialSortOrder);
    const [productCount, setProductCount] = useState(0);
    const [viewMode, setViewMode] = useState();
    const [isLoading, setIsLoading] = useBooleanState(true);
    const [resultsIds, setResultsIds] = useState([]);
    const [selectedSortType, setSelectedSortType] = useState((_c = (_b = queryStrings.get("type")) !== null && _b !== void 0 ? _b : defaultProductType) !== null && _c !== void 0 ? _c : typeOfProducts[0].key.toString());
    const [isBiddingAll, setIsBiddingAll] = useBooleanState(false);
    const [showOnlyActive, setShowOnlyActive] = useBooleanState(false);
    const [loadingFilterCategories, setLoadingFilterCategories] = useState(true);
    const currentPageHandleRef = useRef("");
    // indicates if user was scrolled to remembered position
    const scrolledToProduct = useRef(false);
    // stores remembered id, to scroll user to that position
    const idPosition = useRef("");
    const debouncedMinLotNumber = useDebounce(minLotNumber, 500);
    const isSelected = useCallback((category) => selectedCategories === null || selectedCategories === void 0 ? void 0 : selectedCategories.includes(category.id), [selectedCategories]);
    // SelectedSortType can be equal to 0 or 2
    // 0 means active lots auction page
    // 2 means completed lots auction page
    // we want to hide reactively completed lots on 0-type auction pages
    const checkIfShowOnlyActive = () => {
        if (selectedSortType == "0") {
            setShowOnlyActive(true);
        }
        else {
            setShowOnlyActive(false);
        }
    };
    const unselectCategory = useCallback((category) => {
        // Child categories start with the parent category id,
        // When a parent is removed all children should also be removed
        setLoadedCategories === null || setLoadedCategories === void 0 ? void 0 : setLoadedCategories.removeWhere((v) => v.id.startsWith(category.id));
        setSelectedCategories === null || setSelectedCategories === void 0 ? void 0 : setSelectedCategories.removeWhere((v) => v.startsWith(category.id));
        return true;
    }, []);
    function onSelectedSortOrderChange(sortOrder) {
        setSelectedSortOrder(sortOrder);
        setMinLotNumber("");
        setSortOrderOrLotNumberChanged(!sortOrderOrLotNumberChanged);
    }
    useEffect(() => {
        if (!debouncedMinLotNumber) {
            return;
        }
        setSelectedSortOrder(objectNumberSortOrderKey);
        setSortOrderOrLotNumberChanged(!sortOrderOrLotNumberChanged);
    }, [debouncedMinLotNumber]);
    const filterCategoriesIds = useMemo(() => selectedCategories === null || selectedCategories === void 0 ? void 0 : selectedCategories.join(","), [selectedCategories]);
    const getItems = useCallback(async (page, abort) => {
        checkIfShowOnlyActive();
        readPageAndIdPosition();
        if (page) {
            currentPageHandleRef.current = page;
        }
        let filterData = {
            sortOrder: selectedSortOrder,
            pageHandle: currentPageHandleRef.current ? currentPageHandleRef.current : page,
            filterCategoriesIds,
            categoryId,
            q: searchQuery,
            type: selectedSortType,
            auctionId,
            minLotNumber,
            limit: ItemsPerPageLimit,
            rid: config.rid,
        };
        setIsLoading.toTrue();
        try {
            const result = await auctionApiClient.searchItems(filterData, abort);
            scrollToRememberedIdPosition();
            setProductCount(result.total);
            if (resultsIds.length == 0) {
                setResultsIds(result.items
                    .slice(0, 10)
                    .map((itm) => itm.id.toString()));
            }
            if (result.items.length > 0) {
                const allFromChristiania = result.items.every((item) => CHRISTIANIA_TEST_AUCTION_IDS.includes(item.auctionId));
                if (allFromChristiania) {
                    document.body.classList.add('christiania-test');
                }
                else {
                    document.body.classList.remove('christiania-test');
                }
            }
            else {
                const urlMatch = window.location.pathname.match(/^\/(?:en\/auctions|auksjon)\/(\d+)(?:\/|$)/);
                const auctionIdFromUrl = urlMatch ? parseInt(urlMatch[1], 10) : null;
                const isChristianiaAuctionPage = auctionIdFromUrl !== null && CHRISTIANIA_TEST_AUCTION_IDS.includes(auctionIdFromUrl);
                if (!isChristianiaAuctionPage) {
                    document.body.classList.remove('christiania-test');
                }
            }
            return result;
        }
        finally {
            setIsLoading.toFalse();
        }
    }, [selectedSortType, filterCategoriesIds, sortOrderOrLotNumberChanged]);
    const updateQueryString = useCallback(() => {
        const objectToQuery = {};
        if ((filterCategoriesIds === null || filterCategoriesIds === void 0 ? void 0 : filterCategoriesIds.length) > 0) {
            objectToQuery.filterCategoriesIds = filterCategoriesIds;
        }
        if (selectedSortOrder != startingSortOrder) {
            objectToQuery.sortOrder = selectedSortOrder;
        }
        if (searchQuery) {
            objectToQuery.q = searchQuery;
        }
        if (selectedSortType != startingProductType) {
            objectToQuery.type = selectedSortType;
        }
        pushObjectToState(objectToQuery);
    }, [selectedSortOrder, filterCategoriesIds, selectedSortType]);
    function showSaveSearchModal() {
        if (!document.body.dataset.username) {
            events.openLogin.trigger({ open: true });
            return;
        }
        events.onShowSaveSearch.trigger({
            source: "Auction",
            resultIds: resultsIds,
        });
    }
    async function bidAll() {
        setIsBiddingAll.toggle();
        await (bidAllCtx === null || bidAllCtx === void 0 ? void 0 : bidAllCtx.bidAll());
        setIsBiddingAll.toggle();
    }
    function initializeViewMode() {
        if (localStorage["productListView"]) {
            changeViewMode(localStorage["productListView"]);
        }
        else {
            changeViewMode("grid");
        }
    }
    function changeViewMode(viewType) {
        localStorage["productListView"] = viewType;
        setViewMode(viewType);
    }
    // SCROLL TO ITEM FUNCTIONS
    // Scroll page to remembered id from the state and clear state
    function scrollToRememberedIdPosition() {
        if (scrolledToProduct.current || !idPosition.current)
            return;
        setTimeout(() => {
            // scroll page
            let element = document.getElementById(idPosition.current);
            let top = element ? element.offsetTop : 0;
            window.scroll({ top: top, left: 0, behavior: "smooth" });
            // remove remembered item
            scrolledToProduct.current = true;
            idPosition.current = "";
            if (window.history.state == null)
                return;
            let currentState = window.history.state;
            delete currentState["idPos"];
            delete currentState["offPos"];
            pushObjectToState(currentState);
        }, 500);
    }
    // Save to state clicked item and current offset
    function saveItemPositionState(auctionNumber) {
        var _a;
        let webPageState = window.history.state;
        if (webPageState == null) {
            pushObjectToState({
                idPos: auctionNumber,
                page: currentPageHandleRef.current,
            });
        }
        else {
            webPageState.idPos = auctionNumber;
            webPageState.page = currentPageHandleRef.current;
            pushObjectToState(webPageState);
        }
        localStorage.setItem(SourceAuctionIdStorageKey, (_a = auctionId === null || auctionId === void 0 ? void 0 : auctionId.toString()) !== null && _a !== void 0 ? _a : "");
        localStorage.setItem(AuctionListPageReturnUrlStorageKey, window.location.href);
    }
    // Read if id position and offset position are present in the state
    function readPageAndIdPosition() {
        var _a;
        if (scrolledToProduct.current)
            return;
        // Try get values from query string
        const urlSearchParams = new URLSearchParams(window.location.search);
        const pageHandle = urlSearchParams.get("page");
        idPosition.current = (_a = urlSearchParams.get("idPos")) !== null && _a !== void 0 ? _a : "";
        currentPageHandleRef.current = pageHandle !== null && pageHandle !== void 0 ? pageHandle : "";
        if (window.history.state != null && !idPosition.current) {
            idPosition.current = window.history.state.idPos;
            currentPageHandleRef.current = window.history.state.page;
        }
    }
    function selectSortType(sortType) {
        // SKASD 199 - Set 'sortOrder' to 'Ends last(2); when 'sort type' = 'completed (st 2)'
        if (sortType == "2" && selectedSortOrder != "2") {
            setSelectedSortOrder("2");
        }
        // set 'sortOrder' to 'Ends first (0)' when 'sort type' = 'active(st 0)'.
        else if (sortType == "0" && selectedSortOrder != "0") {
            setSelectedSortOrder("0");
        }
        else if (sortType == "3" &&
            selectedSortOrder != objectNumberSortOrderKey) {
            setSelectedSortOrder(objectNumberSortOrderKey);
        }
        setSelectedSortType(sortType);
    }
    async function fetchFilterCategories() {
        var _a, _b;
        const category = auctionRootCategoryId
            ? await categoriesApiClient.getAuctionCategory(auctionRootCategoryId)
            : await categoriesApiClient.getRootAuctionCategory(auctionId);
        if (!category) {
            return;
        }
        if (auctionRootCategoryId) {
            category.children = (_a = category.children) === null || _a === void 0 ? void 0 : _a.filter(x => x.hasActiveItems);
        }
        setFilterCategories((_b = category.children) !== null && _b !== void 0 ? _b : []);
        setLoadingFilterCategories(false);
    }
    useEffect(() => {
        updateQueryString();
    }, [updateQueryString]);
    useEffect(() => {
        logger.pageView();
        void fetchFilterCategories();
        const handle = (e) => {
            if (e.state.selectedSortOrder) {
                setSelectedSortOrder(e.state.selectedSortOrder);
            }
            if (e.state.selectedCategories) {
                setSelectedCategories(e.state.selectedCategories);
            }
        };
        const handlePageshow = (e) => {
            // flag 'forceReload' is added on auction details page when user places a bid
            // this mechanism is to overrule bf-cache (if persisted == true) and force browser to fetch new data
            if (e.persisted && localStorage["forceReload"]) {
                localStorage.removeItem("forceReload");
                location.reload();
            }
        };
        window.addEventListener("popstate", handle);
        window.addEventListener("pageshow", handlePageshow);
        // Do not "click" input while swiping over (triggered during swipe right navigation on Safari) 
        let input = document.getElementById("gotToInput");
        if (input != null) {
            input.addEventListener("touchmove", function (ev) { ev.preventDefault(); }, false);
        }
        initializeViewMode();
        return () => {
            window.removeEventListener("popstate", handle);
            window.removeEventListener("pageshow", handlePageshow);
        };
    }, []);
    const selectItem = (item) => {
        setLoadedCategories === null || setLoadedCategories === void 0 ? void 0 : setLoadedCategories.append(item);
        setSelectedCategories === null || setSelectedCategories === void 0 ? void 0 : setSelectedCategories.append(item.id);
    };
    const renderDescription = () => {
        if (isSearch) {
            const content = strings.productList.savedSearchesTipContent.split('{BtnName}');
            return (React.createElement("div", { className: "savedSearchesTip" },
                React.createElement("span", { className: "savedSearchesTip-text" },
                    content[0],
                    React.createElement("strong", null,
                        " ",
                        strings.productList.savedSearchesTipBtnName,
                        " "),
                    content[1])));
        }
        return (React.createElement("div", { dangerouslySetInnerHTML: {
                __html: DOMPurify.sanitize(description),
            } }));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("h1", { className: "title" }, name),
        React.createElement("div", { className: "description" }, renderDescription()),
        React.createElement("section", { className: cn("productListingPage-filter", {
                show: showFilterCategories,
            }) },
            React.createElement("div", { className: "productListingPage-filter-content" },
                (filterCategories.length > 0 || loadingFilterCategories) && React.createElement("div", { className: "productListingPage-filter-title" },
                    React.createElement("span", null, strings.productList.filter),
                    React.createElement(ButtonIcon, { onClick: setShowFilterCategories.toFalse },
                        React.createElement(CrossIcon, null))),
                React.createElement("div", { className: "productListingPage-filter-categories" },
                    loadingFilterCategories && React.createElement(Spinner, { size: "small" }),
                    filterCategories.map((item) => (React.createElement(FilterItem, { item: item, key: item.id, isSelected: isSelected, select: selectItem, unselect: unselectCategory, auctionId: auctionId, withActiveItems: !!auctionRootCategoryId }))))),
            React.createElement("div", { className: "productListingPage-filter-footer" },
                React.createElement("button", { className: "button", onClick: setShowFilterCategories.toFalse }, strings.productList.showProductCountResults.replace("{productCount}", productCount.toString())))),
        React.createElement("section", { className: "view-switcher" },
            (bidAllCtx === null || bidAllCtx === void 0 ? void 0 : bidAllCtx.available) && (React.createElement("div", { className: cn(bidModuleStyles.bidAction, styles.bidAllBtn) },
                React.createElement("button", { disabled: isBiddingAll, className: bidModuleStyles.bidButton, onClick: bidAll },
                    React.createElement("span", { style: {
                            visibility: isBiddingAll
                                ? "collapse"
                                : "visible",
                        } }, strings.auctionLot.bidAllBtnLabel),
                    isBiddingAll && (React.createElement(Spinner, { size: "mini", position: "absolute-center" }))))),
            React.createElement("div", { className: "view-switcher-loading" }, isLoading && (React.createElement("span", null,
                React.createElement(Spinner, { size: "small" })))),
            React.createElement("div", { className: "view-switcher-count" },
                React.createElement("span", { className: "view-switcher-count-text" }, strings.productList.productCountResults.replace("{productCount}", productCount.toString()))),
            !!filterCategories.length && (React.createElement("div", { className: "view-switcher-showFilter" },
                React.createElement("button", { className: "button", onClick: setShowFilterCategories.toTrue },
                    strings.productList.showFilters,
                    (selectedCategories === null || selectedCategories === void 0 ? void 0 : selectedCategories.length)
                        ? ` (${selectedCategories.length})`
                        : ""))),
            React.createElement("div", { className: "view-switcher-sorter" },
                React.createElement("select", { value: selectedSortOrder, onChange: (e) => onSelectedSortOrderChange(e.target.value) }, sortOrders.map((item) => (React.createElement("option", { key: item.key, value: item.key }, item.value)))),
                (isSearch ||
                    (auctionId && typeOfProducts.length > 0)) && (React.createElement("select", { value: selectedSortType, onChange: (e) => selectSortType(e.target.value) }, typeOfProducts.map((item) => (React.createElement("option", { key: item.key, value: item.key }, item.value))))),
                isSearch && (React.createElement("button", { className: "button button-ghost view-switcher-sorter-saveSearchBtn", onClick: showSaveSearchModal }, strings.productList.saveSearchBtnLabel))),
            React.createElement("div", { className: "view-switcher-goto " },
                React.createElement("div", { className: "input-group" },
                    React.createElement("input", { id: "gotToInput", className: "input-field", inputMode: "numeric", pattern: "[0-9]*", placeholder: strings.productList.goToLotNumberPlaceholder, value: minLotNumber, onChange: (e) => setMinLotNumber(e.target.value) }))),
            React.createElement("div", { className: "view-switcher-toggle" },
                React.createElement("span", { className: "view-switcher-toggle-text" }, strings.productList.changeView),
                React.createElement("div", { className: "view-switcher-toggle-buttons" },
                    React.createElement("button", { className: cn("button button-tiny", viewMode === "grid"
                            ? "noHover"
                            : "button-ghost"), onClick: () => changeViewMode("grid") }, strings.productList.viewGrid),
                    React.createElement("button", { className: cn("button button-tiny", viewMode === "list"
                            ? "noHover"
                            : "button-ghost"), onClick: () => changeViewMode("list") }, strings.productList.viewList)))),
        React.createElement("div", { className: "product-list-content" },
            !!(loadedCategories === null || loadedCategories === void 0 ? void 0 : loadedCategories.length) && (React.createElement("section", { className: "filter-status" },
                React.createElement("div", { className: "filter-status-label" }, strings.productList.chosenFilters),
                React.createElement("div", { className: "filter-status-choice" },
                    loadedCategories.map((item) => (React.createElement("div", { key: item.id, className: "filter-status-choice-item" },
                        React.createElement("span", null, item.displayName),
                        React.createElement(ButtonIcon, { onClick: () => unselectCategory(item) },
                            React.createElement(CrossIcon, null))))),
                    React.createElement("div", { className: "filter-status-choice-item clear" },
                        React.createElement("button", { onClick: () => setSelectedCategories([]) }, strings.productList.removeAllFilters))))),
            React.createElement("div", { className: "product-listing-row" },
                React.createElement(AuctionLots, { auctionUrl: auctionUrl, auctionStatus: auctionStatus, getItems: getItems, strings: {
                        auctionLot: strings.auctionLot,
                        emptyList: strings.productList.emptyList,
                        pagination: strings.pagination,
                    }, viewMode: viewMode, saveItemPositionState: saveItemPositionState, showOnlyActive: showOnlyActive, itemsPerPageLimit: ItemsPerPageLimit })))));
});
function ButtonIcon({ onClick, children, }) {
    return (React.createElement("button", { className: "button-icon", onClick: onClick }, children));
}
function CrossIcon() {
    return (React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { d: "M15.4414 2.56641L13.6836 0.808594L8 6.47266L2.31641 0.808594L0.558594 2.56641L6.22266 8.25L0.558594 13.9336L2.31641 15.6914L8 10.0273L13.6836 15.6914L15.4414 13.9336L9.77734 8.25L15.4414 2.56641Z", fill: "black" })));
}
